import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback } from 'react';

import { Field, FieldTypes } from '@/components/PersonInPlaceEditor/types';
import useConstants from '@/hooks/useConstants';
import useCountries from '@/hooks/useCountries';
import useInsurancePlans from '@/hooks/useInsurancePlans';
import useInsurancePlanTypes from '@/hooks/useInsurancePlanTypes';
import useInsurancePriorityTypes from '@/hooks/useInsurancePriorityTypes';
import useInsuranceProviders from '@/hooks/useInsuranceProviders';
import useInsuranceRelationships from '@/hooks/useInsuranceRelationships';
import { InsuranceCardImages, InsurancePolicy } from '@/types/insurancePolicy';
import Tooltip from '@/ui/components/Tooltip';

const useInsuranceVerificationFields = ({
  personIdField,
  insuranceCardImages
}: {
  personIdField: boolean;
  insuranceCardImages?: InsuranceCardImages;
}): Field<InsurancePolicy>[] => {
  const ImageTooltip = ({ url }: { url: string }) => (
    <Tooltip
      tooltipContent={<img loading="lazy" src={url} alt="Front" className="w-</Tooltip>50" />}>
      <a className="text-blue-800" target="_blank" rel="noreferrer" href={url}>
        <FontAwesomeIcon icon={faMagnifyingGlass} className="mr-1" />
        Current Image
      </a>
    </Tooltip>
  );

  const { refetch: getInsuranceProviders } = useInsuranceProviders(false);
  const formatInsuranceProviders = useCallback(async () => {
    const { data: insuranceProviders } = await getInsuranceProviders();
    if (insuranceProviders?.data)
      return insuranceProviders.data.map(({ internal_name }) => ({
        label: internal_name,
        value: internal_name
      }));
    return [];
  }, [getInsuranceProviders]);

  const { refetch: getInsurancePlans } = useInsurancePlans(false);
  const { refetch: getInsurancePlanTypes } = useInsurancePlanTypes(false);
  const formatInsurancePlans = useCallback(async () => {
    const { data: insurancePlans } = await getInsurancePlans();
    if (insurancePlans?.data)
      return insurancePlans.data.map(({ name_and_id, id }) => ({
        label: name_and_id,
        value: id
      }));
    return [];
  }, [getInsurancePlans]);

  const { refetch: getConstants } = useConstants(false);
  const formatInsuranceTypes = useCallback(async () => {
    const { data: insurancePlanTypes } = await getInsurancePlanTypes();
    if (insurancePlanTypes?.data)
      return insurancePlanTypes.data.map(({ label, value }) => ({
        label,
        value
      }));
    return [];
  }, [getInsurancePlanTypes]);
  const formatStates = useCallback(async () => {
    const { data: usStates } = await getConstants();
    if (usStates?.data)
      return usStates.data.us_states.map(({ name }) => ({ label: name, value: name }));
    return [];
  }, [getConstants]);

  const { refetch: getInsurancePriorityTypes } = useInsurancePriorityTypes(false);
  const formatInsurancePriorityTypes = useCallback(async () => {
    const { data: priorityTypes } = await getInsurancePriorityTypes();
    if (priorityTypes?.data)
      return priorityTypes.data.map((priorityType) => ({
        label: priorityType,
        value: priorityType
      }));
    return [];
  }, [getInsurancePriorityTypes]);

  const { refetch: getInsuranceRelationships } = useInsuranceRelationships(false);
  const formatInsuranceRelationships = useCallback(async () => {
    const { data: relationships } = await getInsuranceRelationships();
    if (relationships?.data)
      return relationships.data.map((relationship) => ({
        label: relationship,
        value: relationship
      }));
    return [];
  }, [getInsuranceRelationships]);

  const countriesForSelect = useCountries().map((country) => ({
    label: country.name,
    value: country.code
  }));

  return [
    {
      key: 'id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      alwaysSend: personIdField,
      key: 'person_id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      key: 'is_mutable',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      key: 'carrier',
      label: 'Carrier',
      type: FieldTypes.SELECT,
      options: formatInsuranceProviders
    },
    {
      key: 'plan_healthie_id',
      label: 'Plan',
      type: FieldTypes.SELECT,
      options: formatInsurancePlans
    },
    {
      key: 'subscriber_id',
      label: 'Subscriber ID',
      type: FieldTypes.TEXT
    },
    {
      key: 'type',
      label: 'Type',
      type: FieldTypes.SELECT,
      options: formatInsuranceTypes
    },
    {
      key: 'priority_type',
      label: 'Order',
      type: FieldTypes.RADIO,
      options: formatInsurancePriorityTypes
    },
    {
      key: 'plan_start_date',
      label: 'Plan Start Date',
      type: FieldTypes.DATE
    },
    {
      key: 'plan_end_date',
      label: 'Plan End Date',
      type: FieldTypes.DATE
    },
    {
      additionalCaption: {
        label: '',
        value: insuranceCardImages?.front_image_url ? (
          <ImageTooltip url={insuranceCardImages.front_image_url} />
        ) : (
          'No current image'
        )
      },
      key: 'front_image_url' as keyof InsurancePolicy,
      label: 'Upload Insurance Card Front',
      type: FieldTypes.FILE
    },
    {
      additionalCaption: {
        label: '',
        value: insuranceCardImages?.back_image_url ? (
          <ImageTooltip url={insuranceCardImages.back_image_url} />
        ) : (
          'No current image'
        )
      },
      key: 'back_image_url' as keyof InsurancePolicy,
      label: 'Upload Insurance Card Back',
      type: FieldTypes.FILE
    },
    {
      key: 'policyholder_first_name',
      label: 'Policyholder First Name',
      type: FieldTypes.TEXT
    },
    {
      key: 'policyholder_last_name',
      label: 'Policyholder Last Name',
      type: FieldTypes.TEXT
    },
    {
      key: 'relationship',
      label: 'Relationship',
      type: FieldTypes.SELECT,
      options: formatInsuranceRelationships
    },
    {
      key: 'policyholder_dob',
      label: 'Policyholder DOB',
      type: FieldTypes.DATE
    },
    {
      key: 'co_pay',
      label: 'Co-pay (dollars)',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'co_pay_unit',
      label: 'Co-pay unit',
      type: FieldTypes.TEXT
    },
    {
      key: 'annual_deductible',
      label: 'Annual Deductible (dollars)',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'co_insurance',
      label: 'Co-insurance (percentage)',
      type: FieldTypes.PERCENTAGE
    },
    {
      key: 'out_of_pocket_maximum',
      label: 'Out of Pocket Maximum (dollars)',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'insurer_address_line_1',
      label: 'Insurer Address',
      type: FieldTypes.TEXT
    },
    {
      key: 'insurer_address_line_2',
      label: 'Apt, suite, unit, building, floor, etc',
      type: FieldTypes.TEXT
    },
    {
      key: 'insurer_city',
      label: 'City',
      type: FieldTypes.TEXT
    },
    {
      key: 'insurer_state',
      label: 'State',
      type: FieldTypes.SELECT,
      options: formatStates
    },
    {
      key: 'insurer_country',
      label: 'Country',
      type: FieldTypes.SELECT,
      options: countriesForSelect
    },
    {
      key: 'insurer_zip_code',
      label: 'Zip Code',
      type: FieldTypes.TEXT
    },
    {
      key: 'group_id',
      label: 'Group ID',
      type: FieldTypes.TEXT
    },
    {
      key: 'health_plan_id',
      label: 'Health Plan ID',
      type: FieldTypes.TEXT
    },
    {
      key: 'provider_phone_number',
      label: 'Insurance Phone Number',
      type: FieldTypes.TEXT
    },
    {
      key: 'rx_bin',
      label: 'RX Bin',
      type: FieldTypes.TEXT
    },
    {
      key: 'rx_group',
      label: 'RX Group',
      type: FieldTypes.TEXT
    },
    {
      key: 'guarantor',
      label: 'Guarantor',
      type: FieldTypes.TEXT
    }
  ];
};

export default useInsuranceVerificationFields;
